// EPSE Simulations Hook
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EPSESimulation, BESSConfig, EPSEQuarterData } from '@/types/epse';
import { runSimulation } from '@/lib/epseSimulationEngine';
import { toast } from 'sonner';

export function useEPSESimulations(siteId: string | undefined) {
  const queryClient = useQueryClient();
  
  const { data: simulations, isLoading, error } = useQuery({
    queryKey: ['epse-simulations', siteId],
    queryFn: async () => {
      if (!siteId) return [];
      
      const { data, error } = await supabase
        .from('epse_simulations')
        .select('*')
        .eq('site_id', siteId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as unknown as EPSESimulation[];
    },
    enabled: !!siteId,
  });
  
  const runSimulationMutation = useMutation({
    mutationFn: async ({
      siteId,
      name,
      bessConfig,
      dispatchMode,
      quarterData,
      connectionLimit,
    }: {
      siteId: string;
      name: string;
      bessConfig: BESSConfig;
      dispatchMode: 'rule_based' | 'perfect_foresight';
      quarterData: EPSEQuarterData[];
      connectionLimit: number;
    }) => {
      const { data: user } = await supabase.auth.getUser();
      
      // Create simulation record
      const { data: simulation, error: insertError } = await supabase
        .from('epse_simulations')
        .insert({
          site_id: siteId,
          name,
          bess_config: bessConfig as any,
          dispatch_mode: dispatchMode,
          status: 'running',
          created_by: user.user?.id,
        })
        .select()
        .single();
      
      if (insertError) throw insertError;
      
      try {
        // Run simulation
        const { results, kpis } = runSimulation(
          quarterData,
          bessConfig,
          connectionLimit,
          dispatchMode
        );
        
        // Update with results
        const { data: updated, error: updateError } = await supabase
          .from('epse_simulations')
          .update({
            results: results as any,
            kpis: kpis as any,
            status: 'completed',
            completed_at: new Date().toISOString(),
          })
          .eq('id', simulation.id)
          .select()
          .single();
        
        if (updateError) throw updateError;
        return updated as unknown as EPSESimulation;
      } catch (simError) {
        // Mark as failed
        await supabase
          .from('epse_simulations')
          .update({
            status: 'failed',
            error_message: simError instanceof Error ? simError.message : 'Unknown error',
          })
          .eq('id', simulation.id);
        
        throw simError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epse-simulations'] });
      toast.success('Simulatie voltooid');
    },
    onError: (error) => {
      toast.error(`Simulatie mislukt: ${error.message}`);
    },
  });
  
  const deleteSimulation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('epse_simulations')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epse-simulations'] });
      toast.success('Simulatie verwijderd');
    },
    onError: (error) => {
      toast.error(`Fout bij verwijderen: ${error.message}`);
    },
  });
  
  return {
    simulations,
    isLoading,
    error,
    runSimulation: runSimulationMutation,
    deleteSimulation,
  };
}

export function useEPSESimulation(simulationId: string | undefined) {
  return useQuery({
    queryKey: ['epse-simulation', simulationId],
    queryFn: async () => {
      if (!simulationId) return null;
      
      const { data, error } = await supabase
        .from('epse_simulations')
        .select('*')
        .eq('id', simulationId)
        .single();
      
      if (error) throw error;
      return data as unknown as EPSESimulation;
    },
    enabled: !!simulationId,
  });
}
